/**
 * Explore page (X8) -- EXPERT_PORTAL_UX_PLAN.md §8. Real catalog similarity for
 * driver heads and driver shafts, replacing the audited-and-rejected
 * `/search/similar/{type}/{id}` (club_type + loft +/-2 + msrp +/-20%, no
 * MOI/CG/head volume). Public endpoints -- no JWT interceptor needed.
 */
import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1`;

export type ExploreTarget = 'head' | 'shaft';

export interface HeadLookupItem {
  id: number;
  manufacturer: string;
  model: string;
  loft: number | null;
}

export interface ShaftLookupItem {
  id: number;
  shaft_brand: string;
  shaft_model: string;
  standard_flex: string | null;
}

export interface HeadSpec {
  id: number;
  manufacturer: string;
  model: string;
  loft: number | null;
  head_volume: number | null;
  moi_combined: number | null;
  moi_source: string | null;
  cg_distance: string | null;
  cg_height: string | null;
  msrp: number | null;
  similarity: number | null;
}

export interface ShaftSpec {
  id: number;
  shaft_brand: string;
  shaft_model: string;
  standard_flex: string | null;
  weight: number | null;
  torque: number | null;
  standard_cpm: number | null;
  cpm_source: string | null;
  mid_stiffness: number | null;
  mid_stiffness_source: string | null;
  tip_stiffness: string | null;
  butt_stiffness: string | null;
  launch: string | null;
  spin: string | null;
  similarity: number | null;
}

export interface SimilarResponse<T> {
  target: ExploreTarget;
  anchor: T;
  neighbors: T[];
  features: string[];
  population_size: number;
}

export async function lookupCatalog(
  target: ExploreTarget,
  q: string,
  limit = 15,
): Promise<HeadLookupItem[] | ShaftLookupItem[]> {
  const response = await axios.get(`${API_BASE}/explore/lookup/${target}`, {
    params: { q, limit },
  });
  return response.data;
}

export async function getSimilar<T = HeadSpec | ShaftSpec>(
  target: ExploreTarget,
  itemId: number,
  limit = 8,
): Promise<SimilarResponse<T>> {
  const response = await axios.get(`${API_BASE}/explore/similar/${target}/${itemId}`, {
    params: { limit },
  });
  return response.data;
}
